import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable, Injector } from '@angular/core';
import { BehaviorSubject, Observable, tap } from 'rxjs';
import { environment } from 'src/environments/environment';
import { API_URL } from 'src/assets/http/apis';
import { IAuthSession, TokenStorageService } from './token-storage.service';
import { TranslationService } from 'src/app/shared/services/translation.service';
import { UserProfileService } from './user-profile.service';
import { PatientHandoffService } from './patient-handoff.service';

import {
  IOnboardingValidateUserInformation,
  IOnboardingOption,
  IOnboardingCreateUser,
  IOnboardingSendOrResendCode,
  IOnboardingVerifyCode,
  IOnboardingEmailValidation,
  IApiResponse,
  IAuthResponse,
  ILoginRequest,
  IChangePasswordRequest,
  IForgotPasswordRequest,
  IResetPasswordRequest,
} from '../interfaces';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private isAuthenticatedSubject = new BehaviorSubject<boolean | null>(null);
  private currentUserIdSubject = new BehaviorSubject<string | null>(null);

  constructor(
    private http: HttpClient,
    private tokenStorage: TokenStorageService,
    private injector: Injector,
    private userProfileService: UserProfileService,
    private patientHandoff: PatientHandoffService
  ) {
    // Cross-tab sync: when another tab rotates or clears the session,
    // mirror the auth state into this tab so guards / interceptors agree.
    this.tokenStorage.remoteChange$.subscribe(session => {
      this.isAuthenticatedSubject.next(!!session);
    });
  }

  /**
   * @description Initializes the authentication status.
   * @returns {Promise<void>} - A promise that resolves when the authentication status is initialized.
   */
  initAuth(): Promise<void> {
    return new Promise(resolve => {
      this.checkInitialAuthStatus(resolve);
    });
  }

  /**
   * @description Checks the initial authentication status by verifying token existence in localStorage.
   * Token validation will occur naturally when the first authenticated request is made.
   * @param {() => void} done - A callback function to be called when the authentication status is initialized.
   * @returns {void} - A void value.
   */
  private checkInitialAuthStatus(done?: () => void): void {
    this.isAuthenticatedSubject.next(null);

    const hasToken = this.tokenStorage.hasToken();
    this.isAuthenticatedSubject.next(hasToken);

    if (done) done();
  }

  /**
   * @description Checks if user is authenticated.
   * @returns {boolean} - True if token exists.
   */
  isAuthenticated(): Observable<boolean | null> {
    return this.isAuthenticatedSubject.asObservable();
  }

  /**
   * @description Returns the current authentication state synchronously.
   */
  getIsAuthenticated(): boolean | null {
    return this.isAuthenticatedSubject.value;
  }

  /**
   * @description Sets the authentication status.
   * @param {boolean} value - The new authentication status to set.
   */
  setIsAuthenticated(value: boolean): void {
    console.log('[AuthService] setIsAuthenticated:', value);
    this.isAuthenticatedSubject.next(value);
  }

  /**
   * @description Gets the current user ID.
   * @returns {Observable<string | null>} Observable of the current user ID.
   */
  getCurrentUserId(): Observable<string | null> {
    return this.currentUserIdSubject.asObservable();
  }

  /**
   * @description Sets the current user ID and returns an observable for chaining.
   * @param {string} userId - The user ID to set.
   * @returns {Observable<void>} Observable that completes immediately after setting the ID.
   */
  setCurrentUserId(userId: string): Observable<void> {
    return new Observable<void>(observer => {
      this.currentUserIdSubject.next(userId);
      observer.complete();
    });
  }

  /**
   * @description Creates HttpParams with the current language
   * @returns HttpParams with lang parameter set to current language
   * @private
   */
  private getLangParams(): HttpParams {
    const translationService = this.injector.get(TranslationService);
    return new HttpParams().set('lang', translationService.getCurrentLang());
  }

  /**
   * @description Retrieves a list of countries.
   * @returns An Observable of type `IApiResponse<IOnboardingOption[]>` is being returned.
   */
  getCountries(): Observable<IOnboardingOption[]> {
    return this.http.get<IOnboardingOption[]>(`${environment.apiUrl}${API_URL.ONBOARDING.COUNTRIES}`, {
      params: this.getLangParams(),
    });
  }

  /**
   * @description Retrieves a list of specialties.
   * @returns An Observable of type `IApiResponse<IOnboardingOption[]>` is being returned.
   */
  getSpecialties(): Observable<IOnboardingOption[]> {
    return this.http.get<IOnboardingOption[]>(`${environment.apiUrl}${API_URL.ONBOARDING.SPECIALTIES}`, {
      params: this.getLangParams(),
    });
  }

  /**
   * @description Validates user information.
   * @param {IOnboardingValidateUserInformation} validateUserInformation - The `validateUserInformation` parameter in the
   * `validateUserInformation` function is an object that implements the `IOnboardingValidateUserInformation` interface.
   * @returns An Observable of type `IApiResponse<null>` is being returned.
   */
  validateUserInformation(validateUserInformation: IOnboardingValidateUserInformation): Observable<IApiResponse<null>> {
    return this.http.post<IApiResponse<null>>(
      `${environment.apiUrl}${API_URL.ONBOARDING.VALIDATE_USER_INFORMATION}`,
      validateUserInformation
    );
  }

  /**
   * @description Creates and/or registers a new user. When the user supplies a
   * password, the backend returns an immediate `AuthResponse` so the SPA can
   * route them straight to `/home` (the email is already verified by the
   * preceding code-verification step). The legacy magic-link flow returns
   * `data: null` and the caller routes to `/magic-link-sent`.
   */
  createUser(user: IOnboardingCreateUser | null): Observable<IApiResponse<IAuthResponse | null>> {
    const translationService = this.injector.get(TranslationService);
    const userWithLang = user
      ? {
          ...user,
          lang: user.language || translationService.getCurrentLang(),
        }
      : null;
    return this.http
      .post<IApiResponse<IAuthResponse | null>>(`${environment.apiUrl}${API_URL.ONBOARDING.CREATE_USER}`, userWithLang)
      .pipe(
        tap(response => {
          if (response?.data?.accessToken) {
            this.tokenStorage.setAuthSession(toSession(response.data));
            this.isAuthenticatedSubject.next(true);
          }
        })
      );
  }

  /**
   * @description Sends a verification code to the user's email.
   * @param {IOnboardingSendOrResendCode} email - The `email` parameter in the `sendCode` function is an object that implements the `IOnboardingSendOrResendCode` interface.
   * @returns An Observable of type `IApiResponse<null>` is being returned.
   */
  sendCode(email: IOnboardingSendOrResendCode): Observable<IApiResponse<null>> {
    return this.http.post<IApiResponse<null>>(`${environment.apiUrl}${API_URL.ONBOARDING.SEND_CODE}`, email);
  }

  /**
   * @description Resend a verification code to the user's email.
   * @param {IOnboardingSendOrResendCode} user - The `user` parameter in the `resendCode` function is an object that implements the `IOnboardingSendOrResendCode` interface.
   * @returns An Observable of type `IApiResponse<null>` is being returned.
   */
  resendCode(user: IOnboardingSendOrResendCode): Observable<IApiResponse<null>> {
    return this.http.post<IApiResponse<null>>(`${environment.apiUrl}${API_URL.ONBOARDING.RESEND_CODE}`, user);
  }

  /**
   * @description Verifies the verification code.
   * @param {IOnboardingVerifyCode} code - The `code` parameter in the `verifyCode` function is an object that implements the `IOnboardingVerifyCode` interface.
   * @returns An Observable of type `IApiResponse<null>` is being returned.
   */
  verifyCode(code: IOnboardingVerifyCode): Observable<IApiResponse<null>> {
    return this.http.post<IApiResponse<null>>(`${environment.apiUrl}${API_URL.ONBOARDING.VERIFY_CODE}`, code);
  }

  /**
   * @description Verifies the email address to check if it's exist or not.
   * @param {IOnboardingEmailValidation} data - Object containing email and optional language to verify.
   * @returns An Observable of type `IApiResponse<{ isEmailExist: boolean, magicLinkSent: boolean }>` is being returned.
   */
  verifyEmail(
    data: IOnboardingEmailValidation
  ): Observable<IApiResponse<{ isEmailExist: boolean; magicLinkSent: boolean }>> {
    const translationService = this.injector.get(TranslationService);
    const payload: IOnboardingEmailValidation = {
      email: data.email,
      lang: data.lang || translationService.getCurrentLang(),
    };
    return this.http.post<IApiResponse<{ isEmailExist: boolean; magicLinkSent: boolean }>>(
      `${environment.apiUrl}${API_URL.ONBOARDING.EMAIL_VALIDATION}`,
      payload
    );
  }

  /**
   * @description Sends a magic link to the user's email.
   * @param {string} email - is a string that represents the email address to send the magic link to.
   * @returns An Observable of type `IApiResponse<null>` is being returned.
   */
  sendMagicLink(email: string): Observable<IApiResponse<null>> {
    return this.http.post<IApiResponse<null>>(`${environment.apiUrl}${API_URL.ONBOARDING.SEND_MAGIC_LINK}`, { email });
  }

  /**
   * @description Verifies a magic-link token. Since MVP-33 the backend returns
   * the same `AuthResponse` shape as `/auth/login` (15-min access JWT + 7-day
   * refresh token), so the SPA persists all four fields and lets the
   * AuthInterceptor handle silent rotation on 401.
   * @param {string} token - The raw magic-link token from the email URL.
   * @returns An Observable of `IApiResponse<IAuthResponse>`.
   */
  verifyToken(token: string): Observable<IApiResponse<IAuthResponse>> {
    return this.http
      .post<IApiResponse<IAuthResponse>>(
        `${environment.apiUrl}${API_URL.ONBOARDING.VERIFY_TOKEN}`,
        { token },
        {
          headers: new HttpHeaders({
            'Content-Type': 'application/json',
          }),
        }
      )
      .pipe(
        tap(response => {
          if (response?.data?.accessToken && response?.data?.refreshToken) {
            this.tokenStorage.setAuthSession(toSession(response.data));
            this.isAuthenticatedSubject.next(true);
          }
        })
      );
  }

  /**
   * @description Logs out the user.
   * @returns An Observable of type `IApiResponse<null>` is being returned.
   */
  logout(): Observable<IApiResponse<null>> {
    this.currentUserIdSubject.next(null);
    this.userProfileService.clearProfile();
    this.patientHandoff.clear();
    return this.http.post<IApiResponse<null>>(`${environment.apiUrl}${API_URL.ONBOARDING.LOGOUT}`, {});
  }

  /**
   * @description Whether the legacy magic-link flow should still be exposed to users.
   * Hardcoded to `true` for the MVP-2 release (30-day grace period). When a global
   * config service becomes available this should read from it.
   * @returns {boolean} True if the magic-link entry point is visible in the UI.
   */
  isLegacyMagicLinkEnabled(): boolean {
    return true;
  }

  /**
   * @description Authenticates the user with email + password.
   * @param {string} email - User email.
   * @param {string} password - User password.
   * @param {boolean} rememberMe - Whether to extend refresh-token TTL to 30 days.
   * @returns {Observable<IAuthResponse>} The access + refresh tokens and user profile.
   */
  login(email: string, password: string, rememberMe: boolean): Observable<IAuthResponse> {
    const body: ILoginRequest = { email, password, rememberMe };
    return this.http.post<IAuthResponse>(`${environment.apiUrl}${API_URL.AUTH.LOGIN}`, body).pipe(
      tap(response => {
        if (response?.accessToken) {
          this.tokenStorage.setAuthSession(toSession(response));
          this.isAuthenticatedSubject.next(true);
        }
      })
    );
  }

  /**
   * @description Rotates the stored refresh token for a fresh access + refresh
   * pair. Single-flight: the AuthInterceptor relies on this returning the
   * same Observable for concurrent callers, so the caller (the interceptor)
   * gates the call with a `share()`-style guard.
   * @returns {Observable<IAuthResponse>} The rotated session payload.
   */
  refresh(): Observable<IAuthResponse> {
    const refreshToken = this.tokenStorage.getRefreshToken() ?? '';
    return this.http.post<IAuthResponse>(`${environment.apiUrl}${API_URL.AUTH.REFRESH}`, { refreshToken }).pipe(
      tap(response => {
        if (response?.accessToken) {
          this.tokenStorage.setAuthSession(toSession(response));
          this.isAuthenticatedSubject.next(true);
        }
      })
    );
  }

  /**
   * @description Changes the current user's password.
   * Server-side revokes all refresh tokens on success.
   * @param {string} currentPassword - Current password.
   * @param {string} newPassword - New password (must satisfy policy and not match last 5).
   * @returns {Observable<void>} Emits on 204 No Content.
   */
  changePassword(currentPassword: string, newPassword: string): Observable<void> {
    const body: IChangePasswordRequest = { currentPassword, newPassword };
    return this.http.post<void>(`${environment.apiUrl}${API_URL.AUTH.CHANGE_PASSWORD}`, body);
  }

  /**
   * @description Sets the initial password for an authenticated user that has
   * no password yet (legacy magic-link sign-in). The server rejects this call
   * once a password is set; use change-password in that case.
   * @param {string} newPassword - Chosen password (must satisfy the policy).
   * @returns {Observable<void>} Emits on 204 No Content.
   */
  setPassword(newPassword: string): Observable<void> {
    return this.http.post<void>(`${environment.apiUrl}${API_URL.AUTH.SET_PASSWORD}`, { newPassword });
  }

  /**
   * @description Requests a password-reset email. Always returns 200 (enumeration-safe).
   * @param {string} email - Email to send the reset link to (if the account exists).
   * @returns {Observable<{ message: string }>} The constant success payload.
   */
  forgotPassword(email: string): Observable<{ message: string }> {
    const body: IForgotPasswordRequest = { email };
    return this.http.post<{ message: string }>(`${environment.apiUrl}${API_URL.AUTH.FORGOT_PASSWORD}`, body);
  }

  /**
   * @description Consumes a password-reset token and sets a new password.
   * @param {string} token - Raw token from the email link.
   * @param {string} newPassword - Chosen new password (must satisfy policy).
   * @returns {Observable<void>} Emits on 204 No Content.
   */
  resetPassword(token: string, newPassword: string): Observable<void> {
    const body: IResetPasswordRequest = { token, newPassword };
    return this.http.post<void>(`${environment.apiUrl}${API_URL.AUTH.RESET_PASSWORD}`, body);
  }
}

function toSession(response: IAuthResponse): IAuthSession {
  return {
    accessToken: response.accessToken,
    refreshToken: response.refreshToken,
    accessTokenExpiresAt: response.accessTokenExpiresAt,
    refreshTokenExpiresAt: response.refreshTokenExpiresAt,
  };
}
